import { ReactNode } from 'react';
import { useMedia } from 'react-use';
import { useTranslations } from 'next-intl';

import { Theme } from '@silvertours/front-shared';
import { Suppliers as SuppliersView } from '@silvertours/common-landingpages-view';
import { LayoutLegacy } from '@silvertours/front-legacy-entities';

import {
  PrimaryTitle,
  PrimaryWrapper,
  SupplierList,
  SupplierListItem,
  SupplierWrapper,
  StyledThirdPartyLogo,
  StyledInteractiveText,
  SupplierContentGroup,
} from './Suppliers.styles';
import { TrustSeals } from '../TrustSeals';

type SuppliersProps = {
  title?: string;
  suppliers?: SuppliersView;
  includeSearch?: boolean;
  includeTrustSeals?: boolean;
  isMinimal?: boolean;
  isPlain?: boolean;
  author?: ReactNode;
};

const Suppliers = ({
  title,
  suppliers = [],
  includeSearch = true,
  includeTrustSeals = true,
  isMinimal = false,
  isPlain = false,
  author,
}: SuppliersProps) => {
  const isMobile = useMedia(
    `(max-width: ${Theme.getBreakpoint('sm')}px)`,
    true,
  );
  const t = useTranslations('features.suppliers');

  if (!suppliers || suppliers.length === 0) {
    return null;
  }

  const hasTitle = Boolean(title && title.trim().length > 0);
  const isLogoBarMode = !hasTitle;

  const isDesktopOrMinimal = !isMobile || isMinimal;
  const isFullWidthLogoList = isLogoBarMode && !includeTrustSeals;
  const shouldRenderTitleSection = hasTitle && isDesktopOrMinimal;
  const shouldRenderSuppliers = includeSearch && suppliers.length > 0;
  const suppliersIntroduction = t('introText');

  return (
    <LayoutLegacy.FilledSegment id="suppliers">
      {(shouldRenderTitleSection || shouldRenderSuppliers) && (
        <SupplierWrapper
          includeSearch={includeSearch}
          isMinimal={isMinimal}
          isLogoBarMode={isLogoBarMode}
        >
          {/* Legacy Title/Author Block */}
          {shouldRenderTitleSection && (
            <PrimaryWrapper isMinimal={isMinimal}>
              <PrimaryTitle>
                {title}
                {isMinimal && author}
              </PrimaryTitle>
            </PrimaryWrapper>
          )}

          {/* Suppliers Group (Text + Logos) */}
          {shouldRenderSuppliers && (
            <SupplierContentGroup isLogoBarMode={isLogoBarMode}>
              <StyledInteractiveText textColor="grey">
                {suppliersIntroduction}
              </StyledInteractiveText>

              <SupplierList isStandAlone={isFullWidthLogoList}>
                {suppliers.map(({ id }) => (
                  <SupplierListItem key={id}>
                    <StyledThirdPartyLogo
                      name={id}
                      withPadding="none"
                      grayScale
                    />
                  </SupplierListItem>
                ))}
              </SupplierList>
            </SupplierContentGroup>
          )}

          {/* Trust Seals on the right (LogoBarMode only) */}
          {isLogoBarMode && includeTrustSeals && (
            <TrustSeals
              includeSearch={includeSearch}
              isPlainTemplate={isPlain}
            />
          )}
        </SupplierWrapper>
      )}
    </LayoutLegacy.FilledSegment>
  );
};

export { Suppliers };
export type { SuppliersProps };
